'use client'
import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineChevronLeftSmall } from '@qr-front/icons/line-chevron-left-small'
import { LineChevronRightSmall } from '@qr-front/icons/line-chevron-right-small'
import { Button } from '@qr-front/ui/components/Button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@qr-front/ui/components/Select'
import { useBreakpoint } from '@qr-front/ui/hooks'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { Table } from '@tanstack/react-table'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

interface TablePaginationProps<TData> {
	table: Table<TData>
}

const ELLIPSIS = '...'
const ALL_TRANSLATION_KEY = 'dashboard__qr_table__pagination__all'

export function TablePagination<TData>({ table }: TablePaginationProps<TData>) {
	const isDesktop = useBreakpoint('desktop', { ssrFallback: false })
	const t = useTranslations()

	const allLabel = t(ALL_TRANSLATION_KEY)

	const { pageIndex, pageSize } = table.getState().pagination
	const totalEntries = table.getRowCount()
	const totalPages = table.getPageCount()

	const pages = useMemo(() => {
		if (!isDesktop) return [pageIndex + 1]

		const maxVisible = 6
		const pagesArr: (number | string)[] = []
		const current = pageIndex + 1
		const total = totalPages

		if (total <= maxVisible) {
			for (let i = 1; i <= total; i++) pagesArr.push(i)
			return pagesArr
		}

		if (current <= 4) {
			for (let i = 1; i <= 4; i++) pagesArr.push(i)
			pagesArr.push(ELLIPSIS)
			pagesArr.push(total)
			return pagesArr
		}
		if (current >= total - 2) {
			pagesArr.push(1)
			pagesArr.push(ELLIPSIS)
			for (let i = total - 4; i <= total; i++) pagesArr.push(i)
			return pagesArr
		}
		pagesArr.push(1)
		pagesArr.push(ELLIPSIS)
		for (let i = current - 1; i <= current + 1; i++) pagesArr.push(i)
		pagesArr.push(ELLIPSIS)
		pagesArr.push(total)
		return pagesArr
	}, [isDesktop, pageIndex, totalPages])

	const [startEntry, endEntry] = useMemo(() => {
		const start = pageIndex * pageSize + 1
		const end = Math.min((pageIndex + 1) * pageSize, totalEntries)
		return [start, end]
	}, [pageIndex, pageSize, totalEntries])

	const perPageOptions = useMemo<(number | string)[]>(() => {
		const breakpoints = [10, 25, 50]
		const options = breakpoints.filter((bp) => totalEntries > bp)

		return options.length > 0 ? [...options, allLabel] : []
	}, [totalEntries, allLabel])

	const shouldShowPagination = totalEntries > 10

	if (!shouldShowPagination) return null

	return (
		<div className="flex flex-wrap content-center items-center justify-between py-1">
			<div className="flex items-center gap-1 text-body-caption tracking-tight text-grey desktop:type-body-small">
				{t.rich('dashboard__qr_table__pagination__entries', {
					show: (text) => (
						<span className="hidden tracking-normal desktop:inline">
							{text}
						</span>
					),
					start: () => <span className="text-dark-grey">{startEntry}</span>,
					end: () => <span className="text-dark-grey">{endEntry}</span>,
					total: () => <span className="text-dark-grey">{totalEntries}</span>,
				})}
			</div>

			{pageSize !== totalEntries && (
				<div className="flex items-center gap-2 desktop:gap-3">
					<Button
						visual="text"
						size="icon"
						disabled={!table.getCanPreviousPage()}
						onClick={() => table.previousPage()}
						className="h-6 w-8 cursor-pointer"
						aria-label="Previous page"
					>
						<LineChevronLeftSmall width={16} height={16} />
					</Button>

					<div className="relative flex items-center gap-1 desktop:gap-4">
						{pages.map((p, i) =>
							p === ELLIPSIS ? (
								<span key={i} className="text-muted-foreground text-sm px-2">
									{ELLIPSIS}
								</span>
							) : (
								<Button
									key={i}
									visual={p === pageIndex + 1 ? 'default' : 'text'}
									size="default"
									onClick={() => table.setPageIndex(Number(p) - 1)}
									className={cn(
										'relative h-6 min-w-8 overflow-hidden px-3',
										getVersionForClone({
											base: 'rounded-4',
											clone1: 'rounded-full',
										}),
									)}
								>
									<span className="relative z-10">{p}</span>
								</Button>
							),
						)}
					</div>

					<Button
						visual="text"
						size="icon"
						disabled={!table.getCanNextPage()}
						onClick={() => table.nextPage()}
						className="h-6 w-8"
						aria-label="Next page"
					>
						<LineChevronRightSmall width={16} height={16} />
					</Button>
				</div>
			)}

			<Select
				value={pageSize === totalEntries ? allLabel : pageSize.toString()}
				onValueChange={(value) => {
					const newPageSize = value === allLabel ? totalEntries : Number(value)
					table.setPageSize(newPageSize)
				}}
			>
				<SelectTrigger>
					<div className="flex h-6 items-center gap-0.5 text-body-caption tracking-wider text-grey desktop:text-body-small">
						{t.rich('dashboard__qr_table__pagination__per_page', {
							show: (text) => (
								<span className="hidden desktop:inline">{text}</span>
							),
							count: () => (
								<span className="text-dark-grey">
									{pageSize === totalEntries ? allLabel : pageSize}
								</span>
							),
							text: (text) => (
								<span className="mr-1.5 tracking-normal">{text}</span>
							),
						})}
						<LineChevronDownSmall width={16} height={16} />
					</div>
				</SelectTrigger>

				<SelectContent className="mt-2 w-30 rounded-8 bg-white p-4 shadow-generator">
					{perPageOptions.map((option) => (
						<SelectItem
							key={option}
							value={option.toString()}
							className="h-fit px-2 py-[5px] text-dark-grey [&:not(:last-child)]:mb-2"
						>
							{option}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}
